import React from "react";
import BaseAgentHandler from "./BaseAgentHandler";
import AtsAgentInputField from "./AtsAgentInputField";
import JWT from "expo-jwt";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Divider,
  CircularProgress,
  Typography,
  Link,
} from "@material-ui/core";
import FilterListOutlinedIcon from "@material-ui/icons/FilterListOutlined";
import AtsAgentModalHeader from "./AtsAgentModalHeader";
import { ReczeeAtsAgentChip } from "../commons/ReczeeAtsAgent";
import { getCompanyId } from "../../utils/authHelpers";
import { reczeeAlertParams } from "../../utils/commonHelpers";
import { useSnackbar } from "notistack";
import { postRequest } from "../../utils/httpHelpers";
import { getStaticResourceUrl } from "../../utils/urlHelpers";
import PremiumFeatureStarIcon from "../company/PremiumFeatureStarIcon";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles((theme) => ({
  actionDataContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    width: "100%",
    // paddingTop: theme.spacing(3),
    // paddingBottom: theme.spacing(3),
    alignItems: "center",
    fontSize: "18px",
  },
  screenoNotAvailableText: {
    color: "#c40e0e",
    fontSize: "11px",
    fontWeight: "bold",
  },
  filterInputField: {
    textAlign: "left",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& .MuiChip-root": {
      height: "24px",
    },
    "& .MuiChip-deleteIcon": {
      width: "18px",
    },
  },
  dateFieldsRow: {
    justifyContent: "space-between",
    display: "flex",
  },
  datepickerField: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    width: "80%",
    paddingLeft: "2px",
    paddingRight: "2px",
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: "8px",
    },
    "& .MuiIconButton-root": {
      padding: "4px",
    },
  },
  buttonsContainer: {
    marginTop: "12px",
    textAlign: "center",
    width: "100%",
  },
  submit: { marginLeft: "4px", marginRight: "4px" },
  sectionHeader: {
    color: theme.palette.primary.main,
    fontSize: "12px",
    textAlign: "left",
    fontWeight: "bold",
  },
}));

const MIN_GRAD_YEAR = 2000;
const MAX_GRAD_YEAR_DELTA_FROM_TODAY = 10;
const DEFAULT_FILTERS = { include_archived: false };
const DEFAULT_SCREENO_FILTERS = {
  graduation_year: [null, null],
  post_graduation_year: [null, null],
};

let candidateViewOptions = [
  "combined",
  "stage_wise",
  "priority_wise",
  "source_wise",
  "archived",
];

let groupedViewLabels = {
  combined: "All candidates",
  stage_wise: "Stage wise",
  priority_wise: "Priority wise",
  source_wise: "Source wise",
  archived: "Archived",
};

const getDefaultGroupingView = () => {
  const companyId = getCompanyId();
  if (companyId === 56) {
    return "source_wise";
  }
  return "combined";
};

const FilterJobSpecificCandidatesComponent = React.memo(
  ({ parsedPrompt, resetAction }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [isJobResolved, setIsJobResolved] = React.useState(false);
    const [isLoadingFilters, setIsLoadingFilters] = React.useState(false);
    const [showFilters, setShowFilters] = React.useState(false);
    const [requisition, setRequisition] = React.useState(null);
    const [view, setView] = React.useState("candidates");
    const [groupingView, setGroupingView] = React.useState(
      getDefaultGroupingView()
    );
    const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
    const [screenoFilters, setScreenoFilters] = React.useState(
      DEFAULT_SCREENO_FILTERS
    );
    const [searchQuery, setSearchQuery] = React.useState("");
    const [screeningQuestions, setScreeningQuestions] = React.useState([]);

    React.useEffect(() => {
      if (parsedPrompt && parsedPrompt["fields"]["job_data"]) {
        const jobData = parsedPrompt["fields"]["job_data"];
        const userPrompt = parsedPrompt["prompt"];
        setRequisition(jobData);
        setIsJobResolved(true);
        fetchJobFilters(userPrompt, jobData.id);
      }
      console.log("parsedPrompt", parsedPrompt);
    }, [parsedPrompt]);

    const fetchJobFilters = React.useCallback((promptText, requisitionId) => {
      if (!promptText || !requisitionId) return;

      setIsLoadingFilters(true);
      setShowFilters(true);

      postRequest(
        "ats-agent/parse-job-filters",
        {},
        { prompt: promptText, requisition_id: requisitionId },
        (response) => {
          setFilters(response.filters || DEFAULT_FILTERS);
          setScreenoFilters(
            response.screeno_filters || DEFAULT_SCREENO_FILTERS
          );
          setSearchQuery(response.search_query || "");
          setScreeningQuestions(response.screening_questions || []);

          setIsLoadingFilters(false);
        },
        () => {
          setIsLoadingFilters(false);
          setShowFilters(false);
          enqueueSnackbar("Something went wrong!", reczeeAlertParams("error"));
        }
      );
    }, []);

    // console.log("screenoFilters", screenoFilters);
    // console.log("searchQuery", searchQuery);
    // console.log("requisition", requisition);
    // console.log("screeningQuestions", screeningQuestions);
    // console.log("showFilters", showFilters);

    const handleSubmit = () => {
      if (!requisition) {
        enqueueSnackbar(
          "Please select a job you want to filter candidates for!",
          reczeeAlertParams("error")
        );
        return;
      }

      let cleanedFilters = { ...filters };
      Object.keys(cleanedFilters).forEach((k) => {
        if (typeof cleanedFilters[k] === "number") {
          cleanedFilters[k] = cleanedFilters[k].toString();
        }
      });

      let data = {
        view: view || "candidates",
        groupingView: groupingView || getDefaultGroupingView(),
        filters: cleanedFilters || DEFAULT_FILTERS,
        screenoFilters: screenoFilters || DEFAULT_SCREENO_FILTERS,
        searchQuery: searchQuery || "",
      };

      const token = JWT.encode(data, "");
      window.open(`/jobs/${requisition.id}?navigation_token=${token}`);
    };

    return (
      <div className={classes.actionDataContainer}>
        <Typography
          variant="subtitle2"
          className={classes.sectionHeader}
          gutterBottom
        >
          Job
        </Typography>
        <Grid container justifyContent="center" alignItems="center" spacing={1}>
          <Grid item xs={10}>
            <AtsAgentInputField
              fieldName="requisition"
              fieldAttributes={{
                onChange: (_, values) => {
                  setRequisition(values);
                },
                fullWidth: true,
                // label: "Job",
                value: requisition,
                single: true,
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="contained"
              color="primary"
              size={"medium"}
              onClick={() => {
                if (requisition) {
                  fetchJobFilters(parsedPrompt?.prompt || "", requisition.id);
                } else {
                  enqueueSnackbar(
                    "Please select a job!",
                    reczeeAlertParams("error")
                  );
                }
              }}
            >
              Submit
            </Button>
          </Grid>
          {showFilters && (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Grid item xs={12}>
                <Divider style={{ marginTop: 12, marginBottom: 16 }} />
              </Grid>

              {isLoadingFilters ? (
                <Grid
                  item
                  xs={12}
                  style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Auto generating filters...
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionHeader}
                      gutterBottom
                    >
                      Boolean Search
                    </Typography>
                    <AtsAgentInputField
                      fieldName="search_query"
                      fieldAttributes={{
                        value: searchQuery,
                        InputProps: {
                          endAdornment: searchQuery && (
                            <ClearIcon
                              style={{ cursor: "pointer" }}
                              fontSize="small"
                              onClick={() => {
                                setSearchQuery("");
                              }}
                            />
                          ),
                        },
                        onChange: (event) => {
                          setSearchQuery(event.target.value);
                        },
                        style: { marginBottom: "12px", width: "400px" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionHeader}
                      gutterBottom
                    >
                      Candidate Application
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="status"
                      fieldAttributes={{
                        onChange: (_, values) => {
                          setFilters({
                            ...filters,
                            application_states: values,
                          });
                        },
                        fullWidth: true,
                        label: "Application State",
                        value:
                          filters?.application_states?.length > 0
                            ? filters.application_states
                            : [],
                        single: false,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="notice_periods"
                      fieldAttributes={{
                        onChange: (_, values) => {
                          setFilters({
                            ...filters,
                            notice_periods: values,
                          });
                        },
                        fullWidth: true,
                        label: "Notice Period",
                        value:
                          filters?.notice_periods?.length > 0
                            ? filters.notice_periods
                            : [],
                        single: false,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="min_experience"
                      fieldAttributes={{
                        value: filters?.min_experience || "",
                        onChange: (e) => {
                          setFilters({
                            ...filters,
                            min_experience: e.target.value,
                          });
                        },
                        fullWidth: true,
                        label: "Minimum Experience",
                        inputProps: { step: "0.1", min: "0" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="max_experience"
                      fieldAttributes={{
                        value: filters?.max_experience || "",
                        onChange: (e) => {
                          setFilters({
                            ...filters,
                            max_experience: e.target.value,
                          });
                        },
                        fullWidth: true,
                        label: "Maximum Experience",
                        inputProps: { step: "0.1", min: "0" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="min_current_salary"
                      fieldAttributes={{
                        value: filters?.min_current_ctc || "",
                        onChange: (e) => {
                          setFilters({
                            ...filters,
                            min_current_ctc: e.target.value,
                          });
                        },
                        fullWidth: true,
                        label: "Minimum Current Salary",
                        inputProps: { step: "1", min: "0" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="max_current_salary"
                      fieldAttributes={{
                        value: filters?.max_current_ctc || "",
                        onChange: (e) => {
                          setFilters({
                            ...filters,
                            max_current_ctc: e.target.value,
                          });
                        },
                        fullWidth: true,
                        label: "Maximum Current Salary",
                        inputProps: { step: "1", min: "0" },
                      }}
                    />
                  </Grid>

                  {screeningQuestions &&
                  Object.keys(screeningQuestions).length > 0 ? (
                    <>
                      <Grid item xs={12}>
                        <Divider style={{ marginTop: 12, marginBottom: 16 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          className={classes.sectionHeader}
                          gutterBottom
                        >
                          Screening Questions
                        </Typography>
                      </Grid>
                      {Object.keys(screeningQuestions).map((key) => {
                        const q = screeningQuestions[key];

                        switch (q.field_type) {
                          case "number":
                            return (
                              <Grid container key={q.id} spacing={1}>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: "normal" }}
                                  >
                                    {q.title}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <AtsAgentInputField
                                    fieldName="numeric_screening_question"
                                    fieldAttributes={{
                                      value: filters[q.id]?.[0] ?? null,
                                      label: "Minimum",
                                      onChange: (e) =>
                                        setFilters({
                                          ...filters,
                                          [q.id]: [
                                            e.target.valueAsNumber || null,
                                            filters[q.id]?.[1] ?? null,
                                          ],
                                        }),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <AtsAgentInputField
                                    fieldName="numeric_screening_question"
                                    fieldAttributes={{
                                      value: filters[q.id]?.[1] ?? null,
                                      label: "Maximum",
                                      onChange: (e) =>
                                        setFilters({
                                          ...filters,
                                          [q.id]: [
                                            filters[q.id]?.[0] ?? null,
                                            e.target.valueAsNumber || null,
                                          ],
                                        }),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            );
                          case "single_choice":
                          case "multiple_choice":
                            return (
                              <Grid container key={q.id} spacing={1}>
                                <Grid item xs={12}>
                                  <AtsAgentInputField
                                    fieldName={
                                      q.field_type === "multiple_choice"
                                        ? "multiple_choice_screening_question"
                                        : "single_choice_screening_question"
                                    }
                                    fieldAttributes={{
                                      values: Object.values(
                                        q.meta_data.options
                                      ),
                                      value: Array.isArray(filters[q.id])
                                        ? filters[q.id]
                                        : [],
                                      onChange: (_, values) => {
                                        setFilters({
                                          ...filters,
                                          [q.id]: values,
                                        });
                                      },
                                      title: q.title,
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            );
                          case "date":
                            return (
                              <div className={classes.filterInputField}>
                                <Typography
                                  variant="subtitle1"
                                  style={{ fontWeight: "normal" }}
                                >
                                  {q.title}
                                </Typography>
                                <div className={classes.dateFieldsRow}>
                                  <div className={classes.datepickerField}>
                                    <AtsAgentInputField
                                      fieldName="date_screening_question"
                                      fieldAttributes={{
                                        value: filters[q.id]
                                          ? filters[q.id][0]
                                          : null,
                                        onChange: (val) => {
                                          setFilters({
                                            ...filters,
                                            [q.id]: [
                                              val,
                                              !filters[q.id] ||
                                              typeof filters[q.id][1] ===
                                                "undefined"
                                                ? null
                                                : filters[q.id][1],
                                            ],
                                          });
                                        },
                                        label: "Start date",
                                      }}
                                    />
                                  </div>
                                  <div className={classes.datepickerField}>
                                    <AtsAgentInputField
                                      fieldName="date_screening_question"
                                      fieldAttributes={{
                                        value: filters[q.id]
                                          ? filters[q.id][1]
                                          : null,
                                        onChange: (val) => {
                                          setFilters({
                                            ...filters,
                                            [q.id]: [
                                              !filters[q.id] ||
                                              typeof filters[q.id][0] ===
                                                "undefined"
                                                ? null
                                                : filters[q.id][0],
                                              val,
                                            ],
                                          });
                                        },
                                        label: "End date",
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          case "yes_no":
                            return (
                              <Grid container key={q.id} spacing={1}>
                                <Grid item xs={12}>
                                  <AtsAgentInputField
                                    fieldName="yes_no_screening_question"
                                    fieldAttributes={{
                                      value:
                                        filters[q.id] === true
                                          ? "Yes"
                                          : filters[q.id] === false
                                          ? "No"
                                          : null,
                                      onChange: (e) => {
                                        setFilters({
                                          ...filters,
                                          [q.id]: e.target.value,
                                        });
                                      },
                                      title: q.title,
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            );
                          default:
                            return null;
                        }
                      })}
                    </>
                  ) : (
                    ""
                  )}
                  <Grid item xs={12}>
                    <Divider style={{ marginTop: 12, marginBottom: 16 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionHeader}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {requisition?.screeno_enabled ? (
                        <img
                          src={getStaticResourceUrl("/ats-agent-stars.svg")}
                          width="14"
                          height="14"
                          style={{ marginRight: "4px", marginBottom: "0" }}
                        />
                      ) : (
                        ""
                      )}
                      Screeno
                      {requisition?.screeno_enabled ? (
                        ""
                      ) : (
                        <PremiumFeatureStarIcon tooltip="Enable Screeno from settings" />
                      )}
                    </Typography>
                    <span className={classes.screenoNotAvailableText}>
                      {requisition?.screeno_enabled ? (
                        ""
                      ) : (
                        <>
                          Enable Screeno from{" "}
                          <Link
                            href={`/jobs/${requisition.id}/settings`}
                            target="_blank"
                          >
                            job settings
                          </Link>{" "}
                          to auto-populate filters
                        </>
                      )}
                    </span>
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_score"
                      fieldAttributes={{
                        value: screenoFilters["score"],
                        onChange: (_, values) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            score: values,
                          });
                        },
                        label: "Screeno Score",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_tags"
                      fieldAttributes={{
                        value: screenoFilters["tags"],
                        onChange: (_, values) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            tags: values,
                          });
                        },
                        label: "Screeno tags",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_graduation_year"
                      fieldAttributes={{
                        value: screenoFilters["graduation_year"]
                          ? screenoFilters["graduation_year"][0]
                          : null,
                        onChange: (e) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            graduation_year: [
                              e.target.value,
                              screenoFilters.graduation_year[1],
                            ],
                          });
                        },
                        label: "Minimum Graduation Year",
                        inputProps: {
                          step: "1",
                          min: MIN_GRAD_YEAR,
                          max:
                            new Date().getFullYear() +
                            MAX_GRAD_YEAR_DELTA_FROM_TODAY,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_graduation_year"
                      fieldAttributes={{
                        value: screenoFilters["graduation_year"]
                          ? screenoFilters["graduation_year"][1]
                          : null,
                        onChange: (e) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            graduation_year: [
                              screenoFilters.graduation_year[0],
                              e.target.value,
                            ],
                          });
                        },
                        label: "Maximum Graduation Year",
                        inputProps: {
                          step: "1",
                          min:
                            screenoFilters.graduation_year[0] || MIN_GRAD_YEAR,
                          max:
                            new Date().getFullYear() +
                            MAX_GRAD_YEAR_DELTA_FROM_TODAY,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_post_graduation_year"
                      fieldAttributes={{
                        value: screenoFilters["post_graduation_year"]
                          ? screenoFilters["post_graduation_year"][0]
                          : null,
                        onChange: (e) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            post_graduation_year: [
                              e.target.value,
                              screenoFilters.post_graduation_year[1],
                            ],
                          });
                        },
                        label: "Minimum Post Graduation Year",
                        inputProps: {
                          step: "1",
                          min: MIN_GRAD_YEAR,
                          max:
                            new Date().getFullYear() +
                            MAX_GRAD_YEAR_DELTA_FROM_TODAY,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AtsAgentInputField
                      fieldName="screeno_post_graduation_year"
                      fieldAttributes={{
                        value: screenoFilters["post_graduation_year"]
                          ? screenoFilters["post_graduation_year"][1]
                          : null,
                        onChange: (e) => {
                          setScreenoFilters({
                            ...screenoFilters,
                            post_graduation_year: [
                              screenoFilters.post_graduation_year[0],
                              e.target.value,
                            ],
                          });
                        },
                        label: "Maximum Post Graduation Year",
                        inputProps: {
                          step: "1",
                          min:
                            screenoFilters.post_graduation_year[0] ||
                            MIN_GRAD_YEAR,
                          max:
                            new Date().getFullYear() +
                            MAX_GRAD_YEAR_DELTA_FROM_TODAY,
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>
        {showFilters && (
          <>
            <Divider
              style={{ marginTop: 24, marginBottom: 8, width: "100%" }}
            />
            <div className={classes.buttonsContainer}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.submit}
                disabled={isLoadingFilters}
                size={"medium"}
                onClick={() => {
                  resetAction(
                    FilterJobSpecificCandidatesComponent.ACTION,
                    "New Action"
                  );
                }}
              >
                New Action
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isLoadingFilters}
                variant="contained"
                className={classes.submit}
                color="primary"
                size={"medium"}
              >
                Show matching candidates
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }
);

export default class FilterJobSpecificCandidatesHandler extends BaseAgentHandler {
  static ACTION = "filter_job_specific_candidates";

  getModalHeader() {
    return (
      <AtsAgentModalHeader
        icon={<FilterListOutlinedIcon color="primary" />}
        title={this.constructor.getHeaderText()}
      />
    );
  }

  static getHeaderText() {
    return "Filter candidates of a job";
  }

  renderBody() {
    return (
      <FilterJobSpecificCandidatesComponent
        parsedPrompt={this.parsedPrompt}
        resetAction={this.resetAction}
      />
    );
  }

  static renderChip(setInputValue, setExamplePrompt, onChipSelect) {
    return (
      <ReczeeAtsAgentChip
        startIcon={<FilterListOutlinedIcon style={{ color: "#D732A8" }} />}
        onClick={() => {
          setInputValue("Show candidates of a job");
          setExamplePrompt(
            <>
              Show candidates of a job <b>who applied via LinkedIn</b>{" "}
              <b>last week</b>.
            </>
          );
          onChipSelect(this.ACTION);
        }}
        headerText={this.getHeaderText()}
      />
    );
  }

  renderExamplePrompt() {}
}
